import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Check, Pencil, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip'
import { RefreshButton } from '@/shared/components/ui/refresh-button'
import { QueryErrorScreen } from '@/shared/components/ui/query-error-screen'
import { AddressCard } from '@/shared/components/address-card'
import { diffAddressesForUpdate } from '@/shared/utils/diffAddressesForUpdate'
import type { PortalUpdateBusinessInput } from '@/graphql/generated/graphql'
import { Can } from '@/features/auth'
import { routes } from '@/shared/constants'
import { BusinessServicesTab } from '@/features/services'
import { useBusiness } from './hooks/useBusiness'
import { useUpdateBusiness } from './hooks/useUpdateBusiness'
import { BusinessLogoMark } from './components/BusinessLogoMark'
import { BusinessStatusBadge } from './components/BusinessStatusBadge'
import { BusinessOverviewCard } from './components/detail/BusinessOverviewCard'
import { BusinessMetaCard } from './components/detail/BusinessMetaCard'
import { BusinessHistoryTab } from './components/detail/BusinessHistoryTab'
import {
  BusinessEditForm,
  BUSINESS_EDIT_FORM_ID,
} from './components/edit/BusinessEditForm'
import { BusinessDetailPageSkeleton } from './components/detail/BusinessDetailSkeletons'

export function BusinessDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { business, loading, error, refetch } = useBusiness(id)
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const { updateBusiness, loading: saving } = useUpdateBusiness(id ?? '', {
    onSuccess: () => {
      toast.success('Cambios guardados')
      setMode('view')
    },
  })

  if (error) {
    return <QueryErrorScreen error={error} onRetry={() => refetch()} />
  }

  if (loading) {
    return <BusinessDetailPageSkeleton />
  }

  if (!business) {
    return (
      <Card>
        <p className="text-sm text-ink-muted">Negocio no encontrado.</p>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Link
        to={routes.app.businesses.list}
        className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a Mis negocios
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <BusinessLogoMark
            id={business.id}
            name={business.legalName}
            logo={business.logo}
            size="lg"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-serif text-[28px] font-normal leading-[1.1] tracking-tight text-ink sm:text-[36px]">
                {business.legalName}
              </h1>
              {/* Editar negocio = OWNER + MANAGER; el Contable es solo-lectura
                  (goal portal-roles-1 — el gate duro es portalUpdateBusiness). */}
              {mode === 'view' && (
                <Can permission="businesses:edit">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        aria-label="Editar"
                        onClick={() => setMode('edit')}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar</TooltipContent>
                  </Tooltip>
                </Can>
              )}
            </div>
            {business.description && (
              <p className="mt-1 text-sm text-ink-muted">{business.description}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-col items-end gap-1">
            <BusinessStatusBadge status={business.status} />
            {business.plannedDayToPay ? (
              <span className="text-xs text-ink-muted">
                Cobro: día {business.plannedDayToPay} de cada mes
              </span>
            ) : null}
          </div>
          {mode === 'view' && <RefreshButton onClick={async () => { await refetch() }} />}
          {mode === 'edit' && (
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setMode('view')} disabled={saving}>
                <X className="h-4 w-4" />
                Cancelar
              </Button>
              <Button
                type="submit"
                form={BUSINESS_EDIT_FORM_ID}
                variant="primary"
                disabled={saving}
              >
                <Check className="h-4 w-4" />
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          )}
        </div>
      </header>

      {mode === 'edit' ? (
        <BusinessEditForm
          business={business}
          disabled={saving}
          onSubmit={async (values, dirty) => {
            // Solo se envía lo modificado (partial update).
            const input: PortalUpdateBusinessInput = { businessId: business.id }
            if (dirty.legalName) input.legalName = values.legalName
            if (dirty.logo) input.logo = values.logo ? values.logo : null
            // Si se tocó alguna dirección, enviar todas (existentes con su id).
            if (dirty.addresses) {
              const addresses = diffAddressesForUpdate(values.addresses)
              if (addresses.length) input.addresses = addresses
            }

            // Sin cambios → no llamamos la mutación.
            if (!dirty.legalName && !dirty.logo && !dirty.addresses) {
              toast('No hay cambios que guardar')
              setMode('view')
              return
            }
            await updateBusiness(input)
          }}
        />
      ) : (
        <Tabs defaultValue="overview">
          <TabsList className="max-w-full overflow-x-auto">
            <TabsTrigger value="overview">Información general</TabsTrigger>
            <TabsTrigger value="services">Servicios activos</TabsTrigger>
            <TabsTrigger value="history">Historial</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <BusinessOverviewCard business={business} />
                <AddressCard addresses={business.addresses ?? []} />
              </div>
              <BusinessMetaCard business={business} />
            </div>
          </TabsContent>
          <TabsContent value="services">
            <Card>
              <BusinessServicesTab businessId={business.id} />
            </Card>
          </TabsContent>
          <TabsContent value="history">
            <Card>
              <BusinessHistoryTab businessId={business.id} />
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
